import { z } from 'zod';

export const sirenSchema = z
  .string()
  .trim()
  .regex(/^\d{9}$/, 'Le SIREN doit contenir exactement 9 chiffres');

export const siretSchema = z
  .string()
  .trim()
  .regex(/^\d{14}$/, 'Le SIRET doit contenir exactement 14 chiffres');

export const inseeSearchInputSchema = z.object({
  query: z.string().trim().min(2, 'Saisissez au moins 2 caractères').max(120),
  limit: z.number().int().min(1).max(20).default(8),
});

export type InseeSearchInput = z.infer<typeof inseeSearchInputSchema>;

export const inseeSearchResultSchema = z.object({
  siret: z.string(),
  siren: z.string(),
  name: z.string(),
  nafCode: z.string().nullable(),
  legalForm: z.string().nullable(),
  effectifTranche: z.string().nullable(),
  postalCode: z.string().nullable(),
  city: z.string().nullable(),
  addressLine1: z.string().nullable(),
});

export type InseeSearchResult = z.infer<typeof inseeSearchResultSchema>;

export const inseeResolveInputSchema = z.object({
  siret: siretSchema,
});

export type InseeResolveInput = z.infer<typeof inseeResolveInputSchema>;

export const dossierStartInputSchema = z.object({
  source: z.literal('comparateur').default('comparateur'),
  referrer: z.string().max(500).optional(),
  /** Session client (sef_session_id) — unifie le tracking client/serveur (B04) */
  sessionId: z.string().max(128).optional(),
  utm: z
    .object({
      source: z.string().optional(),
      medium: z.string().optional(),
      campaign: z.string().optional(),
      content: z.string().optional(),
      term: z.string().optional(),
    })
    .optional(),
});

export type DossierStartInput = z.infer<typeof dossierStartInputSchema>;

export const dossierCompanyInputSchema = z.object({
  siret: siretSchema,
});

export type DossierCompanyInput = z.infer<typeof dossierCompanyInputSchema>;

export const dossierContactInputSchema = z.object({
  firstName: z.string().trim().min(1, 'Prénom requis').max(100),
  lastName: z.string().trim().min(1, 'Nom requis').max(100),
  email: z.string().trim().toLowerCase().email('Adresse email invalide').max(320),
  // Téléphone optionnel — affiché comme tel dans l'UI (StepContact).
  phoneCountryCode: z
    .string()
    .trim()
    .regex(/^\d{1,4}$/, 'Indicatif invalide')
    .optional(),
  phoneNational: z
    .string()
    .trim()
    .regex(/^[\d\s().+-]+$/, 'Numéro invalide')
    .transform((v) => v.replace(/\D/g, ''))
    .refine((v) => v.length === 0 || (v.length >= 6 && v.length <= 15), 'Numéro invalide')
    .optional(),
});

export type DossierContactInput = z.infer<typeof dossierContactInputSchema>;

export const energyChoiceSchema = z.enum(['electricity', 'gas', 'both']);

export const dossierEnergyInputSchema = z.object({
  energyChoice: energyChoiceSchema,
});

export type DossierEnergyInput = z.infer<typeof dossierEnergyInputSchema>;

export const dossierUsagesInputSchema = z.object({
  sectorProfile: z
    .string()
    .trim()
    .min(1)
    .max(60)
    .regex(/^[a-z0-9_]+$/, 'Profil sectoriel invalide')
    .nullable(),
  usageIds: z
    .array(
      z
        .string()
        .trim()
        .min(1)
        .max(60)
        .regex(/^[a-z0-9_]+$/, 'Identifiant d’usage invalide'),
    )
    .max(40, 'Trop d’usages sélectionnés')
    .default([]),
  notes: z.string().trim().max(1000).optional(),
});

export type DossierUsagesInput = z.infer<typeof dossierUsagesInputSchema>;

export const billSeasonSchema = z.enum(['summer', 'winter']);

export type BillSeason = z.infer<typeof billSeasonSchema>;

const ACCEPTED_BILL_TYPES = [
  'application/pdf',
  'image/jpeg',
  'image/png',
  'image/webp',
  'image/heic',
  'image/heif',
] as const;

export const dossierUploadUrlRequestSchema = z.object({
  season: billSeasonSchema,
  filename: z.string().trim().min(1).max(255),
  contentType: z.enum(ACCEPTED_BILL_TYPES, {
    errorMap: () => ({
      message: 'Format accepté : PDF, JPG, PNG, WEBP, HEIC (max 10 Mo)',
    }),
  }),
  size: z
    .number()
    .int()
    .positive()
    .max(10 * 1024 * 1024, 'Fichier trop volumineux (max 10 Mo)'),
});

export type DossierUploadUrlRequest = z.infer<typeof dossierUploadUrlRequestSchema>;

export const dossierBillAckSchema = z.object({
  season: billSeasonSchema,
  key: z.string().trim().min(1).max(500),
  filename: z.string().trim().min(1).max(255),
  contentType: z.string().trim().min(1).max(120),
  size: z.number().int().positive(),
});

export type DossierBillAck = z.infer<typeof dossierBillAckSchema>;

export const dossierRemindLaterSchema = z.object({
  reason: z
    .enum(['bills_not_available', 'no_time', 'other'])
    .default('bills_not_available'),
  consentEmail: z.boolean().refine((v) => v === true, {
    message: 'Vous devez accepter de recevoir le lien de reprise par email.',
  }),
});

export type DossierRemindLaterInput = z.infer<typeof dossierRemindLaterSchema>;

export const dossierSubmitInputSchema = z.object({
  consentRgpd: z.boolean().refine((v) => v === true, {
    message:
      'Vous devez accepter le traitement de vos données pour finaliser votre demande.',
  }),
  consentMarketing: z.boolean().default(false),
  message: z.string().trim().max(2000).optional(),
});

export type DossierSubmitInput = z.infer<typeof dossierSubmitInputSchema>;

/* ────────────────────────────────────────────────────────────────────────────
   Bill Insights — résultat de l'analyse facture (OCR + heuristiques)
──────────────────────────────────────────────────────────────────────────── */

export const billInsightsTaxesSchema = z.object({
  cspe: z.boolean(),
  cspeAmountEur: z.number().nullable(),
  turpe: z.boolean(),
  turpeAmountEur: z.number().nullable(),
  tva: z.boolean(),
  cta: z.boolean(),
  ticgn: z.boolean(),
  acciseGaz: z.boolean(),
});

export const billInsightsSchema = z.object({
  source: z.enum(['pdf-text', 'ocr', 'unknown']),
  confidence: z.enum(['high', 'medium', 'low']),
  energyType: z.enum(['electricity', 'gas', 'unknown']),
  supplierName: z.string().max(100).nullable(),
  annualConsumptionKwh: z.number().nonnegative().max(100_000_000).nullable(),
  subscribedPowerKva: z.number().nonnegative().max(100_000).nullable(),
  totalAmountEur: z.number().nonnegative().max(10_000_000).nullable(),
  supplyAmountHt: z.number().nonnegative().max(10_000_000).nullable(),
  routingAmountEur: z.number().nonnegative().max(10_000_000).nullable(),
  unitPriceEurPerKwh: z.number().nonnegative().max(10).nullable(),
  contractType: z.enum(['fixed', 'indexed', 'regulated', 'unknown']),
  contractExpiryDate: z.string().max(10).nullable(),
  overPowerSignal: z.boolean(),
  taxes: billInsightsTaxesSchema,
  billPeriod: z.string().max(8).nullable(),
  pointDeliveryId: z.string().max(20).nullable(),
  suggestedLevers: z.array(z.enum(['turpe', 'cspe'])),
  savingsScore: z.number().int().min(0).max(100),
});

export type BillInsightsPayload = z.infer<typeof billInsightsSchema>;

export const dossierBillInsightsInputSchema = z.object({
  season: z.enum(['summer', 'winter']),
  insights: billInsightsSchema,
});

export type DossierBillInsightsInput = z.infer<typeof dossierBillInsightsInputSchema>;

export const comparateurDossierPublicStatusSchema = z.enum([
  'draft',
  'awaiting_bills',
  'submitted',
  'converted',
  'abandoned',
]);

export const comparateurDossierPublicSchema = z.object({
  resumeToken: z.string(),
  status: comparateurDossierPublicStatusSchema,
  company: z
    .object({
      siret: z.string(),
      siren: z.string().nullable(),
      name: z.string(),
      nafCode: z.string().nullable(),
      legalForm: z.string().nullable(),
      employeeCount: z.string().nullable(),
      postalCode: z.string().nullable(),
      city: z.string().nullable(),
    })
    .nullable(),
  contact: z
    .object({
      firstName: z.string().nullable(),
      lastName: z.string().nullable(),
      email: z.string().nullable(),
      phoneE164: z.string().nullable(),
      phoneCountryCode: z.string().nullable(),
      phoneNational: z.string().nullable(),
    })
    .nullable(),
  energyChoice: energyChoiceSchema.nullable(),
  usages: z
    .object({
      sectorProfile: z.string().nullable(),
      usageIds: z.array(z.string()),
      notes: z.string().nullable(),
    })
    .nullable(),
  bills: z.object({
    summer: z
      .object({
        uploadedAt: z.string(),
        filename: z.string(),
        contentType: z.string(),
        size: z.number(),
      })
      .nullable(),
    winter: z
      .object({
        uploadedAt: z.string(),
        filename: z.string(),
        contentType: z.string(),
        size: z.number(),
      })
      .nullable(),
    requested: z.boolean(),
  }),
  /** Insights extraits par l'analyse OCR/heuristique côté client */
  billInsights: z
    .object({
      summer: billInsightsSchema.nullable(),
      winter: billInsightsSchema.nullable(),
    })
    .nullable(),
  expiresAt: z.string(),
  submittedAt: z.string().nullable(),
});

export type ComparateurDossierPublic = z.infer<typeof comparateurDossierPublicSchema>;

export const COMPARATEUR_ACCEPTED_BILL_TYPES = ACCEPTED_BILL_TYPES;
export const COMPARATEUR_MAX_BILL_SIZE_BYTES = 10 * 1024 * 1024;
